import React, { useEffect, useMemo, useRef, useState } from 'react';
import { localToday } from '../../../utils/localToday';
import { createPortal } from 'react-dom';
import { getBackendURL } from '../../../components/env';
import { getSupabase, supabaseSchema } from '../../../components/supabaseClient';
import ProcessSourceList from './ProcessSourceList';
import ProcessDiffReview from './ProcessDiffReview';
import MasterNoteDiffModal from './MasterNoteDiffModal';
import { locateEdits, locateSectionEdits, composeOps } from './applyEditsToDoc';
import { skillBadgeLabel } from '../../../utils/aiChatSettings';
import { beginActivity, endActivity } from '../../../utils/activityTracker';
import { bearerAuthHeaders } from '../../../utils/backendAuth';

// Trim selected source items to the shape the backend expects. Inlined from
// the removed processCopilotSources helper -- now that Process Forms is the
// canonical engine for everything (no separate "Copilot output" mode), this
// is a simple pass-through.
function buildRequestItems(selectedSourceItems) {
  return selectedSourceItems.map((item) => ({
    type: item.type,
    id: item.id,
    label: item.label,
    date: item.date || null,
    duration: item.duration ?? null,
  }));
}

// The feedback endpoint reports the real cause of a failure -- almost always a
// Supabase/PostgREST rejection, e.g. a column the backend writes that is missing
// from this tenant schema's ai_feedback table -- in `details`. Surface it, else
// every failure reads as a bare 500 and needs a server-log dig to tell apart.
async function feedbackFailureReason(res) {
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON body */ }
  const detail = body && (body.details || body.error);
  return detail ? `${detail} (${res.status})` : `Request failed (${res.status})`;
}

const key = (t, id) => `${t}:${id}`;
const REVIEW_MIN_WIDTH = 520;
const REVIEW_MIN_HEIGHT = 320;

function defaultReviewWindow() {
  if (typeof window === 'undefined') {
    return { left: 120, top: 84, width: 760, height: 520 };
  }
  return {
    left: Math.max(24, window.innerWidth - 800),
    top: 84,
    width: Math.min(760, Math.max(REVIEW_MIN_WIDTH, window.innerWidth - 48)),
    height: Math.min(520, Math.max(REVIEW_MIN_HEIGHT, window.innerHeight - 140)),
  };
}

// Right-docked layout used when ProcessModal opens via autoGenerate (Audio
// Manager's "Process this recording"). Pins the review pane to the right
// edge of the chart where the Copilot tab already lives — strategic align
// with Day 6 PM2: audio diffs belong in the right panel, not a floating
// modal that gets occluded by the recorder popup window. The user can still
// drag/resize after if they want, but the initial position never hides
// behind the recorder popup or covers the note.
function rightDockedReviewWindow() {
  if (typeof window === 'undefined') {
    return { left: 1080, top: 84, width: 420, height: 720 };
  }
  const width = Math.max(REVIEW_MIN_WIDTH, Math.min(440, Math.round(window.innerWidth * 0.30)));
  const top = 84;
  const height = Math.max(REVIEW_MIN_HEIGHT, window.innerHeight - top - 24);
  return {
    left: Math.max(24, window.innerWidth - width - 16),
    top,
    width,
    height,
  };
}

function buildDefaultSourceState(groups) {
  const sel = new Set();
  const exp = new Set();
  for (const g of groups) {
    const hasNew = g.items.some((i) => !i.processed);
    const hasProcessed = g.items.some((i) => i.processed);
    for (const i of g.items) if (!i.processed) sel.add(key(i.type, i.id));
    if (hasNew && hasProcessed) exp.add(g.key || g.type);
  }
  return { selectedIds: sel, expanded: exp };
}

// Exported so other surfaces (MedStudentClinicPage's toolbar Process
// button auto-park flow, future "Process the latest recording" quick-
// actions) can compute the same default selection without re-running
// ProcessModal's full state machine. Inputs are the live `groups` from
// useVisitSources; outputs match ProcessModal's open-transition effect.
export function computeDefaultSourceSelection(groups) {
  const { selectedIds, expanded } = buildDefaultSourceState(groups || []);
  return {
    selectedIds: Array.from(selectedIds),
    expanded: Array.from(expanded),
  };
}

function truncateText(value, max = 1200) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}\n...` : text;
}

function summarizeEditPreview(debug) {
  return Array.isArray(debug?.edit_preview) ? debug.edit_preview.slice(0, 3) : [];
}

// "Aug 17, 2026, 3:42 PM" in the reader's own locale/zone — a saved diff is
// read on a different device, often in a different timezone, than the one it
// was written on, so the stamp must render locally rather than as stored UTC.
function formatSavedAt(iso) {
  if (!iso) return '';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// Id for one Master Note run. The backend keeps the run under it (the saved
// Master Note), so a repeat returns the stored result instead of running again.
function newMasterNoteRequestId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// "on your phone" / "on the web" for a run started by the other app.
function runOrigin(run) {
  return run?.client === 'mobile' ? ' on your phone' : '';
}

function patientValue(patient, keys) {
  for (const k of keys) {
    const value = patient?.[k];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

function stringifyEditsForFeedback(edits) {
  try {
    return JSON.stringify(edits.map((edit) => ({
      id: edit.id,
      kind: edit.kind,
      section: edit.section,
      anchor: edit.anchor,
      oldText: edit.oldText,
      newText: edit.newText,
      rationale: edit.rationale,
      source: edit.source,
    })));
  } catch (_) {
    return String(edits || '');
  }
}

function buildDiffFeedbackPrompt({ patient, documentText, selectedItems, editCount }) {
  const sources = (selectedItems || []).map((item, index) => {
    const parts = [
      `${index + 1}. ${item.label || item.id || 'Source'}`,
      `type=${item.type || ''}`,
      `id=${item.id || ''}`,
    ];
    if (item.date) parts.push(`date=${item.date}`);
    if (item.duration !== undefined && item.duration !== null && item.duration !== '') parts.push(`duration=${item.duration}`);
    return parts.join('; ');
  }).join('\n');
  const doc = truncateText(documentText, 2000) || '(empty document)';
  return [
    `Process diff feedback for ${patient?.type || 'document'} (${editCount} generated edit${editCount === 1 ? '' : 's'}).`,
    '',
    'CURRENT DOCUMENT',
    doc,
    '',
    'SELECTED SOURCES',
    sources || '(none)',
  ].join('\n');
}

function ResponsePreview({ debug }) {
  const items = summarizeEditPreview(debug);
  if (!items.length) {
    return (
      <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
        (no edit preview)
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
      {items.map((item, index) => {
        const section = item?.section ? `[${item.section}] ` : '';
        const kind = item?.kind ? `${item.kind}: ` : '';
        const oldText = String(item?.oldText || '').trim();
        const newText = String(item?.newText || '').trim();
        return (
          <div key={`${item?.kind || 'edit'}-${item?.section || 'section'}-${index}`} className="space-y-1">
            <div className="font-medium text-slate-700 dark:text-slate-200">
              {index + 1}. {section}{kind}
            </div>
            <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-200">
              {oldText ? (
                <span className="rounded bg-rose-100 px-1 py-0.5 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200">
                  {oldText}
                </span>
              ) : null}
              {oldText && newText ? ' ' : null}
              {newText ? (
                <span className="rounded bg-emerald-100 px-1 py-0.5 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                  {newText}
                </span>
              ) : null}
              {!oldText && !newText ? '(no text preview)' : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function summarizeTruncation(debug) {
  const info = debug?.truncation_info;
  if (!info) return [];
  const messages = Array.isArray(info.messages) ? info.messages.filter(Boolean) : [];
  if (messages.length) return messages;
  if (!info.truncated_count && !info.skipped_count) return ['No source truncation was reported.'];
  return [
    `Truncated: ${info.truncated_count || 0}`,
    `Skipped: ${info.skipped_count || 0}`,
  ];
}

function ProcessDebugPanel({ debug, onClose }) {
  if (!debug) return null;
  const truncationLines = summarizeTruncation(debug);
  const sources = Array.isArray(debug.sources) ? debug.sources : [];
  const attemptErrors = Array.isArray(debug.attempt_errors) ? debug.attempt_errors : [];

  return (
    <div className="absolute inset-4 z-30 flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div>
          <h3 className="text-sm font-semibold">AI debug summary</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Captured from the backend response for this diff generation.</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
          Close
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-auto px-4 py-4 text-sm">
        <section className="space-y-1">
          <div className="font-semibold">Provider and model</div>
          <div>Model used: {debug.model_used || debug.model_id || 'Unknown'}</div>
          {Array.isArray(debug.requested_models) && debug.requested_models.length > 0 && (
            <div>Tried in order: {debug.requested_models.join(', ')}</div>
          )}
          {debug.total_tokens != null && <div>Tokens: {debug.input_tokens || 0} in / {debug.output_tokens || 0} out / {debug.total_tokens} total</div>}
          {debug.cost_usd != null && <div>Estimated cost: ${Number(debug.cost_usd || 0).toFixed(6)}</div>}
        </section>

        <section className="space-y-1">
          <div className="font-semibold">Prompt preview</div>
          <pre className="overflow-auto rounded-lg bg-slate-50 p-3 text-xs whitespace-pre-wrap dark:bg-slate-800/60">{truncateText(debug.system_prompt_preview || '(no system prompt preview)')}</pre>
          <pre className="overflow-auto rounded-lg bg-slate-50 p-3 text-xs whitespace-pre-wrap dark:bg-slate-800/60">{truncateText(debug.user_prompt_preview || '(no user prompt preview)')}</pre>
        </section>

        <section className="space-y-1">
          <div className="font-semibold">Response preview</div>
          <ResponsePreview debug={debug} />
        </section>

        <section className="space-y-1">
          <div className="font-semibold">Truncation</div>
          <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
            {truncationLines.map((line) => <div key={line}>{line}</div>)}
          </div>
        </section>

        <section className="space-y-1">
          <div className="font-semibold">Source summary</div>
          <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
            <div>Document type: {debug.document_type || 'Unknown'}</div>
            <div>Selected items: {debug.selected_item_count ?? 0}</div>
            <div>Selected item types: {(debug.selected_item_types || []).join(', ') || 'None'}</div>
            <div>Parsed edits: {debug.parsed_edit_count ?? 0}</div>
            <div>Copilot fallback edits: {debug.copilot_action_item_fallback_count ?? 0}</div>
            <div>Final edits returned: {debug.final_edit_count ?? 0}</div>
          </div>
          {sources.length > 0 && (
            <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800/60">
              {sources.map((source, index) => (
                <div key={`${source.type || 'source'}-${source.title || index}`}>
                  {(source.title || 'Untitled source')} {source.type ? `(${source.type})` : ''}
                </div>
              ))}
            </div>
          )}
        </section>

        {attemptErrors.length > 0 && (
          <section className="space-y-1">
            <div className="font-semibold">Fallback and errors</div>
            <div className="rounded-lg bg-amber-50 p-3 text-xs dark:bg-amber-500/10">
              {attemptErrors.map((item, index) => (
                <div key={`${item.model_id || 'model'}-${index}`}>
                  {item.model_id || 'Unknown model'}: {item.error || 'Unknown error'}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/** {type, id} for each source, whatever extra fields the caller carried. */
function sourceRefs(items) {
  return (Array.isArray(items) ? items : [])
    .filter((i) => i && i.type && i.id !== undefined && i.id !== null)
    .map((i) => ({ type: i.type, id: i.id }));
}

export default function ProcessModal({
  isOpen, onClose, patient, patientId, documentText, groups = [], loading,
  userRole = 'medstudent', onApplyEdits, onTemplateOff,
  aiRequestSettings = {},
  // Per-user chat "skill" (Settings › Skills in the AI chat), surfaced here
  // too because Generate Master Note is where it visibly shapes the note.
  // Same preference, same storage — onSkillChange lifts the edit to the chart
  // page, which persists it to users.ai_chat_settings and feeds it back down
  // through aiRequestSettings on the next generate.
  activeSkill = null,
  skillIntensity = 'full',
  onSkillChange = null,
  // Optional: if provided, ONLY pre-check the audio source matching this
  // session_id on open (instead of the default "auto-check every unprocessed
  // source" behavior). Used by the Audio Manager modal's per-row Process
  // button so the user can re-process a SPECIFIC prior recording even after
  // a hard refresh has cleared the recorder popup's session state.
  pinAudioSessionId = null,
  // Stage 2c — same shape for reports: when set, pre-check ONLY the report
  // whose id matches this file path. Lets the Reports modal's per-row
  // "Process this report" button skip the source picker the same way Audio
  // Manager's button does. id matches the full storage file path (see
  // useVisitSources reportItems — report.id is the full path).
  pinReportPath = null,
  // When true (with ONE of the pin props set), skip the source-picker UI
  // entirely and fire fill-template engine immediately on open. The intent
  // was already declared upstream (user clicked "Process this recording"
  // or "Process this report"); an intermediate picker is just friction.
  // The diff modal appears as soon as the engine returns.
  autoGenerate = false,
  // Phase B (MVP) — when provided AND autoGenerate is true, render the
  // review screen INTO this DOM node (a slot inside MedStudentRightPanel's
  // Copilot tab area) instead of a floating window over the note. Removes
  // the z-order fight with the external recorder popup window and aligns
  // with the Day 6 PM2 plan: audio diffs belong in the right panel.
  // diffPanelSlotRef should be a React ref; we read .current at portal time.
  diffPanelSlotRef = null,
  // "Park in sidebar" — when provided, the review screen header shows a
  // button that lifts the current review state (edits, approvals, debug,
  // selected sources, document text) up to the parent via this callback,
  // then closes the modal. The parent renders the parked review inside
  // the right sidebar as its own tab so the user can keep working on the
  // note while the diff waits for review.
  onParkReview = null,
  // Unpark state — when provided, the modal consumes this once on its
  // next open (source-phase fields: selectedIds, expanded, useTemplate;
  // review-phase fields: edits, approvals, generatedRequestItems) and
  // seeds local state from it. The parent typically sets this as the
  // payload of a parked review when the user clicks "Unpark" on the
  // right bar's Review tab. After the modal consumes the payload, the
  // parent should clear unparkState so re-opens don't re-apply.
  unparkState = null,
  // When true, a review is ALREADY parked in the right bar (Review tab).
  // The Park-in-sidebar button is hidden in this case because clicking
  // it would silently overwrite the existing parked payload with
  // whatever's in the modal right now — a confusing UX where the user
  // loses their old parked state without warning. The user has to
  // Dismiss the parked review first if they really want to park new
  // state in its place. Mirrors MedStudentClinicPage.parkedReview !== null.
  hasParkedReview = false,
  // Generation state — owned by the parent (MedStudentClinicPage) so
  // the live "Generating…" timer survives the unpark/park cycle. When
  // the user clicks Generate and then parks/unparks mid-fetch, the
  // surfacing surface (sidebar / modal / sidebar) all keep ticking from
  // the same parent-owned start timestamp instead of resetting to 0:00.
  // ProcessModal reads isGenerating for button disabled state + label
  // and calls onGenerationBegin/onGenerationEnd around the fetch so
  // the parent's flag stays in sync. Passes generationStartedAt to
  // ProcessSourceList so the elapsed counter computes elapsed seconds
  // (now − startTime) instead of restarting a local interval.
  isGenerating = false,
  generationStartedAt = null,
  onGenerationBegin = null,
  onGenerationEnd = null,
  // Fires once after the modal consumes `unparkState`. Parent uses this
  // to reset its unparkState so a later re-open doesn't re-seed the
  // modal with stale selections.
  onUnparkConsumed = null,
  // Master Note flow (Leejoe 2026-07-01 playground): when the physician
  // accepts the Master Note preview, this callback replaces the entire
  // note content in the parent Quill editor with the given string. The
  // parent (MedStudentClinicPage) is responsible for the yjs-aware
  // setContents pattern per feedback_yjs_setcontents_doubles memory —
  // ProcessModal just hands over the text.
  onReplaceNote = null,
  // Phase of the parent's currently-parked payload ('sources' | 'review' |
  // 'master' | null). Lets the master-completion handler avoid clobbering a
  // parked REVIEW (real approve/deny work) while still upgrading a parked
  // sources/master payload with the finished note.
  parkedPhase = null,
  // Task #280 — the open chart's patient-matched live transcript (parent
  // verifies the patientId). Backend uses it as a fallback when saved-source
  // aggregation is thin because "Save Live Transcript" was never clicked.
  liveTranscript = '',
  // ── Saved Master Note diff (Omar 2026-08-17) ──────────────────────────
  // One saved diff per user per document in the files bucket, so a note
  // generated on the iPad can be reviewed and accepted at the desktop. This
  // replaced the old "Keep for later" button, which parked the note in React
  // state only and lost it on reload.
  // The backend now keeps every run there (pass `documentPath` and each
  // Generate is saved before the reply), so there is no Save button: a run
  // whose reply this page lost — reload, closed tab — shows here as saved, as
  // does one still running (`runningRun`) or started on the phone.
  // The CHART owns the round-trip (useSavedMasterDiff)
  // because its header badge needs the same answer without opening this
  // modal; here we only render it.
  documentPath = null,
  savedDiff = null,
  runningRun = null,
  failedRun = null,
  onDiscardSavedDiff = null,
  // Re-read the saved Master Note (after a run, or a reply that never came).
  onSavedRunRefresh = null,
  // Accept replaced the note with run `requestId` (null for an unsaved one, or
  // a restored saved one from before runs had ids — `{ restoredSaved }`): the
  // chart clears the saved Master Note, which is in the note now.
  onAcceptedMasterNote = null,
  // Fired when a saved diff is restored into the preview. The chart uses it to
  // claim the note's origin for its Master Note PHI guard — a restored diff
  // comes from THIS document's own folder, but it never goes through the
  // Generate button that normally stamps that origin.
  onOpenSavedDiff = null,
  savedDiffError = null,
}) {
  const [screen, setScreen] = useState('sources');
  // "Caveman · full" (or null) — badge text for every screen's header.
  const skillHeaderLabel = skillBadgeLabel(activeSkill, skillIntensity);
  const [useTemplate, setUseTemplate] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  // Master Note flow — the whole-note text the engine returned, held between
  // fetch completion and the physician clicking Accept/Cancel on the preview.
  const [masterNoteOutput, setMasterNoteOutput] = useState('');
  const [masterNoteMetadata, setMasterNoteMetadata] = useState(null);
  // The sources the previewed master note was built from, as {type, id}.
  // Accept hands them to the chart so they are stamped processed — the same
  // ledger Process's Apply stamps — instead of staying "new" forever.
  const [masterNoteSources, setMasterNoteSources] = useState([]);
  // Read-only word-diff overlay (MasterNoteDiffModal) opened from the preview
  // header's "View diff" button. Display-only — no effect on generation/Accept.
  const [showMasterDiff, setShowMasterDiff] = useState(false);
  // Note text a RESTORED saved diff was generated against. null whenever the
  // preview holds a freshly generated note (that one was diffed against the
  // live document). A diff only means anything against its own base, so the
  // restored preview compares against this, not against whatever the chart
  // happens to hold now — and warns when the two have drifted apart.
  const [restoredBaseText, setRestoredBaseText] = useState(null);
  // savedAt of the write this modal just made. `savedDiff` from the parent is
  // the source of truth, but the round-trip lands a beat later and the button
  // should confirm immediately.
  const [justSavedAt, setJustSavedAt] = useState(null);
  // The saved run the preview shows (its requestId), so Accept clears exactly
  // that one; null for a note that was never saved.
  const [masterNoteRequestId, setMasterNoteRequestId] = useState(null);
  // Latest isOpen, readable from async closures — the master fetch keeps
  // running after the user parks/closes (component stays mounted), and its
  // completion handler needs to know whether the modal is still visible.
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  // True while THIS modal's master-note fetch is running (set/cleared inside
  // generate()). Distinct from the parent's shared isGenerating flag, which
  // also covers the parked diff flow.
  const masterGenInFlightRef = useRef(false);
  // `generating` is now parent-owned (`isGenerating` prop). The parent
  // tracks both the running flag AND the start timestamp so the live
  // "Generating…" timer survives the unpark/park cycle without
  // resetting to 0:00. See the generate() body below for the
  // onGenerationBegin/onGenerationEnd call sites.
  const [applying, setApplying] = useState(false);
  const [edits, setEdits] = useState([]);
  const [approvals, setApprovals] = useState({});
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  // Task #229 (Leejoe 2026-06-29): dual-mode diff state (diffsByMode,
  // activeMode, modeErrors) and the ModeTabs UI were removed for demo
  // simplicity. Rewrite is the only mode shipped. See
  // project_diff_modes_future.md for the parking-lot decision.
  const [showDebug, setShowDebug] = useState(false);
  const [generatedRequestItems, setGeneratedRequestItems] = useState([]);
  const [diffFeedbackStatus, setDiffFeedbackStatus] = useState(null);
  const [diffFeedbackText, setDiffFeedbackText] = useState('');
  const [submittingDiffFeedback, setSubmittingDiffFeedback] = useState(false);
  const [diffFeedbackSubmitted, setDiffFeedbackSubmitted] = useState(false);
  const [diffFeedbackError, setDiffFeedbackError] = useState(null);
  const [masterFeedbackStatus, setMasterFeedbackStatus] = useState(null);
  const [masterFeedbackText, setMasterFeedbackText] = useState('');
  const [submittingMasterFeedback, setSubmittingMasterFeedback] = useState(false);
  const [masterFeedbackSubmitted, setMasterFeedbackSubmitted] = useState(false);
  const [masterFeedbackError, setMasterFeedbackError] = useState(null);
  const [reviewWindow, setReviewWindow] = useState(defaultReviewWindow);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  // Seed defaults ONCE per open: auto-check unprocessed items, auto-expand mixed
  // groups. The parent re-renders frequently (autosave, collab presence, comment
  // bubbles) and hands us a fresh `groups` array each time; re-seeding on every
  // such render would wipe the user's expand/checkbox choices a few hundred ms
  // after they make them (this is what made the Reports expand button look
  // broken). So we seed only on the open transition, and as async sources (e.g.
  // reports) load in afterward we *merge* the newly-arrived unprocessed items
  // into the selection without disturbing anything the user has already touched.
  const openedRef = useRef(false);
  const knownIdsRef = useRef(new Set());
  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    const allItems = groups.flatMap((g) => g.items);
    if (!openedRef.current) {
      openedRef.current = true;
      const defaults = buildDefaultSourceState(groups);
      // If the caller pinned a specific audio (e.g. via Audio Manager's
      // per-row Process button), override the default unprocessed-only
      // selection: pre-check ONLY that recording so the user just hits
      // Generate diff. Match against either `id` or any sessionId-like
      // field the audio item exposes.
      let initialSelection = defaults.selectedIds;
      if (pinAudioSessionId) {
        const pin = String(pinAudioSessionId);
        const pinned = allItems.find((i) => {
          if (i.type !== 'audio') return false;
          if (String(i.id) === pin) return true;
          const meta = i.metadata || {};
          if (meta.session_id && String(meta.session_id) === pin) return true;
          if (meta.audio_index != null && String(meta.audio_index) === pin) return true;
          if (meta.url && String(meta.url) === pin) return true;
          return false;
        });
        if (pinned) {
          initialSelection = new Set([key(pinned.type, pinned.id)]);
        }
      } else if (pinReportPath) {
        // Stage 2c — Reports modal's "Process this report" button. Report
        // items use the full storage file path as `id` (useVisitSources).
        const pin = String(pinReportPath);
        const pinned = allItems.find((i) => i.type === 'report' && String(i.id) === pin);
        if (pinned) {
          initialSelection = new Set([key(pinned.type, pinned.id)]);
        }
      }
      setSelectedIds(initialSelection);
      setExpanded(defaults.expanded);
      setScreen('sources');
      setEdits([]);
      setApprovals({});
      setError(null);
      setDebugInfo(null);
      setShowDebug(false);
      setGeneratedRequestItems([]);
      setDiffFeedbackStatus(null);
      setDiffFeedbackText('');
      setSubmittingDiffFeedback(false);
      setDiffFeedbackSubmitted(false);
      setDiffFeedbackError(null);
      setMasterFeedbackStatus(null);
      setMasterFeedbackText('');
      setSubmittingMasterFeedback(false);
      setMasterFeedbackSubmitted(false);
      setMasterFeedbackError(null);
      setReviewWindow(autoGenerate ? rightDockedReviewWindow() : defaultReviewWindow());
      knownIdsRef.current = new Set(allItems.map((i) => key(i.type, i.id)));

      // ── Unpark payload consumed HERE ─────────────────────────────────
      // When the user clicks "Unpark" on the right bar's Review tab, the
      // parent hands us the parked payload so the user can keep working
      // in the modal. We seed local state from it:
      //   • source-phase: selectedIds / expanded / useTemplate
      //   • review-phase: edits / approvals / generatedRequestItems +
      //                   jumps screen='review' so ProcessDiffReview
      //                   shows the AI's diff immediately
      // After consuming, the parent's onUnparkConsumed callback fires so
      // it can clear unparkState (avoids re-seeding on subsequent opens).
      if (unparkState && typeof unparkState === 'object') {
        try {
          if (Array.isArray(unparkState.selectedIds)) {
            setSelectedIds(new Set(unparkState.selectedIds));
          }
          if (Array.isArray(unparkState.expanded)) {
            setExpanded(new Set(unparkState.expanded));
          }
          if (typeof unparkState.useTemplate === 'boolean') {
            setUseTemplate(unparkState.useTemplate);
          }
          if (Array.isArray(unparkState.edits) && unparkState.edits.length > 0) {
            setEdits(unparkState.edits);
            setApprovals(unparkState.approvals || {});
            setDebugInfo(unparkState.debugInfo || null);
            setGeneratedRequestItems(unparkState.generatedRequestItems || []);
            setScreen('review');
          }
          //   • master-phase: masterNoteOutput → jumps screen='master-preview'
          //     so the clinician lands back on Accept/Cancel where they left.
          if (typeof unparkState.masterNoteOutput === 'string' && unparkState.masterNoteOutput) {
            setMasterNoteOutput(unparkState.masterNoteOutput);
            setMasterNoteMetadata(unparkState.masterNoteMetadata || null);
            setMasterNoteSources(sourceRefs(unparkState.selectedItems));
            setMasterNoteRequestId(unparkState.masterNoteRequestId || null);
            // This note was generated against the LIVE document, so drop any
            // base carried over from a previously restored saved diff — the
            // modal stays mounted across closes, so that state can outlive
            // the preview it belonged to.
            setRestoredBaseText(null);
            setJustSavedAt(null);
            setScreen('master-preview');
          }
        } catch (_e) { /* non-fatal — defaults remain */ }
        // Tell the parent we're done with the unpark payload — clears
        // unparkState so re-opens don't re-seed. Deferred so React
        // finishes the initial render before the parent clears state.
        setTimeout(() => {
          if (typeof onUnparkConsumed === 'function') {
            try { onUnparkConsumed(); } catch (_e) { /* non-fatal */ }
          }
        }, 0);
      }
    } else {
      // Already open — only act on items that have appeared since the last pass
      // (async load). Auto-check the new unprocessed ones; never remove or
      // collapse what the user has chosen.
      const fresh = allItems.filter((i) => !knownIdsRef.current.has(key(i.type, i.id)));
      if (fresh.length) {
        setSelectedIds((prev) => {
          const n = new Set(prev);
          for (const i of fresh) if (!i.processed) n.add(key(i.type, i.id));
          return n;
        });
        for (const i of fresh) knownIdsRef.current.add(key(i.type, i.id));
      }
    }
    // We deliberately key only on isOpen / groups / unparkState here:
    //   • isOpen     — reset openedRef + re-seed defaults on every open
    //   • groups     — re-trigger auto-check of new sources while open
    //   • unparkState — consume the unpark payload on the open transition
    // autoGenerate / pinAudioSessionId / pinReportPath / onUnparkConsumed
    // are read inside the open transition (the only consumer) so they're
    // safe to omit — re-firing on those would trigger a redundant seed.
  }, [isOpen, groups, unparkState]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedSourceItems = useMemo(
    () => groups.flatMap((g) => g.items)
      .filter((i) => selectedIds.has(key(i.type, i.id))),
    [groups, selectedIds],
  );
  const selectedItems = useMemo(
    () => selectedSourceItems.map((i) => ({ type: i.type, id: i.id })),
    [selectedSourceItems],
  );

  // ── Saved diff: restore / save / discard ────────────────────────────────
  // The note the preview is diffing against: a restored save carries its own
  // base, a freshly generated note was built from the live document.
  const previewBaseText = restoredBaseText !== null ? restoredBaseText : documentText;
  // Only a restored diff can go stale — a fresh one was just generated from
  // what the chart holds right now.
  const savedDiffStale = restoredBaseText !== null
    && String(restoredBaseText || '').trim() !== String(documentText || '').trim();
  const savedAtStamp = justSavedAt || savedDiff?.savedAt || null;

  // Hand a saved diff back to the full Master Note preview — same screen,
  // same Accept & replace, so reviewing a saved note is the flow the user
  // already knows.
  const openSavedDiff = () => {
    if (!savedDiff?.proposedText) return;
    if (typeof onOpenSavedDiff === 'function') {
      try { onOpenSavedDiff(savedDiff); } catch (_e) { /* parent handled */ }
    }
    setMasterNoteOutput(savedDiff.proposedText);
    setMasterNoteMetadata(savedDiff.metadata || null);
    setMasterNoteSources(sourceRefs(savedDiff.selectedItems));
    setRestoredBaseText(typeof savedDiff.baseText === 'string' ? savedDiff.baseText : null);
    setMasterNoteRequestId(savedDiff.requestId || null);
    setScreen('master-preview');
  };

  const handleDiscardSavedDiff = async () => {
    if (typeof onDiscardSavedDiff !== 'function') return;
    await onDiscardSavedDiff();
    setJustSavedAt(null);
  };

  // Leaving the preview drops the restored base — the next note that lands
  // here is a fresh one, diffed against the live document.
  const leaveMasterPreview = () => {
    setMasterNoteOutput('');
    setMasterNoteMetadata(null);
    setMasterNoteSources([]);
    setRestoredBaseText(null);
    setJustSavedAt(null);
    setMasterNoteRequestId(null);
  };

  // Auto-generate: when ProcessModal was opened with a pinned audio + the
  // autoGenerate flag (e.g. from Audio Manager's "Process this recording"),
  // immediately fire the engine instead of waiting for a click in the source
  // picker. Guarded by autoGenFiredRef so it only runs once per open and only
  // when the pinned selection has actually been seeded. The generate function
  // is defined just below; this effect calls it via the ref-trampoline.
  const generateRef = useRef(null);
  const autoGenFiredRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { autoGenFiredRef.current = false; return; }
    if (autoGenFiredRef.current) return;
    if (!autoGenerate) return;
    // Fire on either pin — Audio Manager's button OR Reports modal's button.
    if (!pinAudioSessionId && !pinReportPath) return;
    if (isGenerating || screen !== 'sources') return;
    if (selectedSourceItems.length === 0) return; // wait until the pinned source is selected
    autoGenFiredRef.current = true;
    // Defer to next tick so any pending state from the seed effect lands first.
    setTimeout(() => { generateRef.current && generateRef.current(); }, 0);
  }, [isOpen, autoGenerate, pinAudioSessionId, pinReportPath, selectedSourceItems, isGenerating, screen]);

  const toggleItem = (t, id) => setSelectedIds((s) => {
    const n = new Set(s); const k = key(t, id); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const toggleGroup = (groupKey) => setSelectedIds((s) => {
    const n = new Set(s);
    const g = groups.find((x) => (x.key || x.type) === groupKey);
    if (!g) return n;
    const all = g.items.every((i) => n.has(key(i.type, i.id)));
    for (const i of g.items) { const k = key(i.type, i.id); all ? n.delete(k) : n.add(k); }
    return n;
  });
  const toggleExpand = (groupKey) => setExpanded((s) => {
    const n = new Set(s); n.has(groupKey) ? n.delete(groupKey) : n.add(groupKey); return n;
  });
  const resetSelection = () => {
    const defaults = buildDefaultSourceState(groups);
    setSelectedIds(defaults.selectedIds);
    setExpanded(defaults.expanded);
  };
  // Task #151 — explicit bulk selection controls Janu asked for on
  // 2026-06-27 dogfood call: a "Select new only" preset (auto-checks every
  // un-processed source, unchecks anything already processed) and a
  // "Clear all" (unchecks everything so the clinician can hand-pick
  // surgically). Reset already does the new-only behavior under the hood
  // but the button label was opaque; these aliases make the intent
  // explicit + add the missing complementary clear.
  const selectOnlyNew = () => {
    const next = new Set();
    for (const g of groups) {
      for (const i of g.items) {
        if (!i.processed) next.add(key(i.type, i.id));
      }
    }
    setSelectedIds(next);
  };
  const clearAllSelection = () => setSelectedIds(new Set());

  const generate = async () => {
    if (!useTemplate) { onTemplateOff(selectedItems); onClose(); return; }
    // Generation state lives on the parent — flip its flag + capture
    // start timestamp so both this modal AND the right-bar Review tab
    // (when the same flow is parked mid-fetch) tick from the same
    // anchor instead of resetting locally on each remount.
    if (typeof onGenerationBegin === 'function') {
      try { onGenerationBegin(); } catch (_) { /* non-fatal */ }
    }
    setError(null);
    // Keep generateRef in sync so the auto-generate effect (above) can fire
    // this function via setTimeout without ordering hazards.
    beginActivity('processing_document');
    try {
      // Master generation is in flight — close paths use this to park a
      // GENERATING placeholder so the run never disappears from view.
      masterGenInFlightRef.current = true;
      const selectedRequestItems = buildRequestItems(selectedSourceItems);
      setGeneratedRequestItems(selectedRequestItems);
      const sourcesUsed = sourceRefs(selectedItems);

      // Master Note flow (Leejoe 2026-07-01): the Update Note button now
      // calls the whole-note rewrite engine (/api/master-note/) instead of
      // suggest-edits. The engine returns ONE integrated note that reads
      // as if written fresh today; no JSON-edits pipeline, no anchor-based
      // insertion, no orphaned-old-content duplication risk. The response
      // lands in the 'master-preview' screen where the physician can see
      // it side-by-side with the current note and click Accept to replace
      // the editor content wholesale (via onReplaceNote), or Cancel to bail.
      //
      // Backend flattens selectedItems into a labeled multi-source transcript
      // via PatientDocumentProcessor (same aggregation path suggest-edits
      // uses), so this same payload shape works without any frontend-side
      // source content extraction.
      // With the note's path the backend keeps this run as the saved Master
      // Note before it replies (a run can take minutes), so a reply this page
      // never gets — reload, closed tab, dropped connection — is read back
      // from the saved Master Note instead of lost.
      const requestId = documentPath ? newMasterNoteRequestId() : null;
      const refreshSavedRun = () => {
        if (typeof onSavedRunRefresh === 'function') {
          try { onSavedRunRefresh(); } catch (_) { /* non-fatal */ }
        }
      };
      let res;
      try {
        res = await fetch(`${getBackendURL()}/api/master-note/`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeaders()) },
          body: JSON.stringify({
            patientId, patientData: patient, schema: supabaseSchema,
            userRole, documentType: patient?.type || null,
            documentContent: documentText, selectedItems: selectedRequestItems,
            liveTranscript: liveTranscript || undefined,
            // The clinician's date, not the UTC server's — see utils/localToday.
            clientToday: localToday(),
            model_choice: 1,
            ...(requestId ? { requestId, documentPath } : {}),
            ...aiRequestSettings,
          }),
        });
      } catch (networkError) {
        if (!requestId) throw networkError;
        refreshSavedRun();
        throw new Error('Lost the connection while the Master Note was generating. If it finishes, it appears here as your saved Master Note.');
      }
      let body = null;
      try { body = await res.json(); } catch (_) { /* non-JSON body */ }
      if (!res.ok) {
        if (res.status === 409 && body?.error === 'generation_in_progress') {
          refreshSavedRun();
          throw new Error('A Master Note for this note is already being generated (in another tab or on your phone). It appears here when it finishes.');
        }
        if (requestId) refreshSavedRun();
        const reason = (body && body.error) ? body.error : `Request failed (${res.status})`;
        throw new Error(reason);
      }
      if (!body) throw new Error('Empty response from server');
      if (body.error) throw new Error(body.error);
      const filledTemplate = String(body.filled_template || '').trim();
      if (!filledTemplate) {
        throw new Error('Master Note returned an empty response.');
      }
      setMasterNoteOutput(filledTemplate);
      setMasterNoteMetadata(body.metadata || null);
      setMasterNoteSources(sourcesUsed);
      // Fresh note — built from the live document, so it owns the comparison
      // base. Any restored-saved-diff base is stale from here on.
      setRestoredBaseText(null);
      setJustSavedAt(requestId ? (body.run?.savedAt || null) : null);
      setMasterNoteRequestId(requestId);
      if (requestId) refreshSavedRun();
      setDebugInfo(body.metadata || null);
      setShowDebug(false);
      if (!isOpenRef.current && typeof onParkReview === 'function' && parkedPhase !== 'review') {
        // Finished while the modal was parked/closed — hand the result to the
        // right bar as a parked MASTER payload so nothing is lost and nothing
        // reroutes (Leejoe 2026-07-07: "make sure it doesn't change when we
        // park it but keeps generating master note the way we want it").
        // A parked REVIEW payload (real approve/deny work) is never clobbered.
        onParkReview({
          phase: 'master',
          masterNoteOutput: filledTemplate,
          masterNoteMetadata: body.metadata || null,
          masterNoteRequestId: requestId,
          selectedItems,
          documentText,
        });
      }
      setScreen('master-preview');
      setDiffFeedbackStatus(null);
      setDiffFeedbackText('');
      setDiffFeedbackSubmitted(false);
      setDiffFeedbackError(null);
      setMasterFeedbackStatus(null);
      setMasterFeedbackText('');
      setMasterFeedbackSubmitted(false);
      setMasterFeedbackError(null);
    } catch (e) {
      setError(e.message || 'Failed to generate edits.');
    } finally {
      masterGenInFlightRef.current = false;
      // Mirror the parent's flag back to false + drop the start
      // timestamp so the timer chip disappears on whichever surface
      // is currently rendered (modal OR right bar sidebar).
      if (typeof onGenerationEnd === 'function') {
        try { onGenerationEnd(); } catch (_) { /* non-fatal */ }
      }
      endActivity('processing_document');
    }
  };

  // Keep generateRef synced with the latest closure so the autoGenerate
  // effect (above) calls the current version that closes over up-to-date
  // selectedSourceItems / aiRequestSettings / etc.
  generateRef.current = generate;

  const submitDiffFeedback = async () => {
    if (submittingDiffFeedback) return;
    if (!diffFeedbackStatus) {
      setDiffFeedbackError('Select Yes or No first.');
      return;
    }
    setSubmittingDiffFeedback(true);
    setDiffFeedbackError(null);
    try {
      const supabase = await getSupabase();
      const { data: { session } = {} } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const userJwtToken = session?.access_token || null;
      if (!userId) throw new Error('User not authenticated');

      let username = session?.user?.email || null;
      try {
        const { data: userData } = await supabase
          .from('users')
          .select('name, email')
          .eq('id', userId)
          .single();
        username = userData?.name || userData?.email || username;
      } catch (_) {
        // Keep the session email fallback.
      }

      const res = await fetch(`${getBackendURL()}/api/ai-assistant/feedback/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          patientId,
          sessionId: `process_diff_${patientId}_${Date.now()}`,
          prompt: buildDiffFeedbackPrompt({
            patient,
            documentText,
            selectedItems: generatedRequestItems,
            editCount: edits.length,
          }),
          response: stringifyEditsForFeedback(edits),
          feedbackText: diffFeedbackText,
          status: diffFeedbackStatus,
          patientName: patientValue(patient, ['patient_name', 'name']),
          username,
          scheduledDate: patientValue(patient, ['date_', 'scheduled_date', 'date']),
          schema: supabaseSchema,
          userJwtToken,
          metadata: {
            source: 'process_diff',
            documentType: patient?.type || null,
            editCount: edits.length,
            selectedItems: generatedRequestItems,
          },
        }),
      });
      if (!res.ok) throw new Error(await feedbackFailureReason(res));
      setDiffFeedbackSubmitted(true);
      setDiffFeedbackText('');
    } catch (e) {
      setDiffFeedbackError(e.message || 'Failed to submit feedback.');
    } finally {
      setSubmittingDiffFeedback(false);
    }
  };

  const submitMasterFeedback = async () => {
    if (submittingMasterFeedback) return;
    if (!masterFeedbackStatus) {
      setMasterFeedbackError('Select Yes or No first.');
      return;
    }
    setSubmittingMasterFeedback(true);
    setMasterFeedbackError(null);
    try {
      const supabase = await getSupabase();
      const { data: { session } = {} } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const userJwtToken = session?.access_token || null;
      if (!userId) throw new Error('User not authenticated');

      let username = session?.user?.email || null;
      try {
        const { data: userData } = await supabase
          .from('users')
          .select('name, email')
          .eq('id', userId)
          .single();
        username = userData?.name || userData?.email || username;
      } catch (_) {
        // Keep the session email fallback.
      }

      const res = await fetch(`${getBackendURL()}/api/ai-assistant/feedback/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          patientId,
          sessionId: `process_master_note_${patientId}_${Date.now()}`,
          prompt: buildDiffFeedbackPrompt({
            patient,
            documentText,
            selectedItems: generatedRequestItems,
            editCount: generatedRequestItems.length,
          }),
          response: masterNoteOutput,
          feedbackText: masterFeedbackText,
          status: masterFeedbackStatus,
          patientName: patientValue(patient, ['patient_name', 'name']),
          username,
          scheduledDate: patientValue(patient, ['date_', 'scheduled_date', 'date']),
          schema: supabaseSchema,
          userJwtToken,
          metadata: {
            source: 'process_master_note',
            documentType: patient?.type || null,
            selectedItems: generatedRequestItems,
            modelUsed: masterNoteMetadata?.model_used || null,
            outputTokens: masterNoteMetadata?.output_tokens || null,
          },
        }),
      });
      if (!res.ok) throw new Error(await feedbackFailureReason(res));
      setMasterFeedbackSubmitted(true);
      setMasterFeedbackText('');
    } catch (e) {
      setMasterFeedbackError(e.message || 'Failed to submit feedback.');
    } finally {
      setSubmittingMasterFeedback(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const ops = composeOps(documentText, edits, approvals);
      await onApplyEdits(ops, selectedItems);
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to apply edits.');
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    if (!isOpen || screen !== 'review') return undefined;
    const onMove = (e) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (dragRef.current) {
        const nx = Math.max(0, Math.min(vw - 80, e.clientX - dragRef.current.offsetX));
        const ny = Math.max(0, Math.min(vh - 40, e.clientY - dragRef.current.offsetY));
        setReviewWindow((p) => ({ ...p, left: nx, top: ny }));
      } else if (resizeRef.current) {
        const r = resizeRef.current;
        const dx = e.clientX - r.startX;
        const dy = e.clientY - r.startY;
        let newW = r.startW;
        let newH = r.startH;
        let newLeft = r.startLeft;
        let newTop = r.startTop;

        if (r.side.includes('w')) {
          const target = r.startW - dx;
          newW = Math.max(REVIEW_MIN_WIDTH, Math.min(r.startLeft + r.startW, target));
          newLeft = (r.startLeft + r.startW) - newW;
        } else if (r.side.includes('e')) {
          const target = r.startW + dx;
          newW = Math.max(REVIEW_MIN_WIDTH, Math.min(vw - r.startLeft - 8, target));
          newLeft = r.startLeft;
        }
        if (r.side.includes('n')) {
          const target = r.startH - dy;
          newH = Math.max(REVIEW_MIN_HEIGHT, Math.min(r.startTop + r.startH, target));
          newTop = (r.startTop + r.startH) - newH;
        } else if (r.side.includes('s')) {
          const target = r.startH + dy;
          newH = Math.max(REVIEW_MIN_HEIGHT, Math.min(vh - r.startTop - 8, target));
          newTop = r.startTop;
        }

        setReviewWindow({
          left: newLeft,
          top: newTop,
          width: newW,
          height: newH,
        });
      }
    };
    const onUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isOpen, screen]);

  const startDragging = (e) => {
    if (e.target.closest('button, input, a')) return;
    dragRef.current = {
      offsetX: e.clientX - reviewWindow.left,
      offsetY: e.clientY - reviewWindow.top,
    };
    e.preventDefault();
  };

  const startResizing = (side) => (e) => {
    resizeRef.current = {
      side,
      startX: e.clientX,
      startY: e.clientY,
      startW: reviewWindow.width,
      startH: reviewWindow.height,
      startLeft: reviewWindow.left,
      startTop: reviewWindow.top,
    };
    e.preventDefault();
    e.stopPropagation();
  };

  if (!isOpen) return null;

  // Tailwind utilities in this app are scoped to `#clinic-root` (tailwind.config
  // `important: '#clinic-root'`), so the modal MUST be portaled inside that
  // element or every class on it is inert and it renders invisible. Fall back to
  // body only if the root isn't found.
  const portalTarget =
    (typeof document !== 'undefined' && document.getElementById('clinic-root')) ||
    (typeof document !== 'undefined' ? document.body : null);
  if (!portalTarget) return null;

  if (screen === 'master-preview') {
    // Master Note preview — whole-note rewrite from /api/master-note/. Side-by-side
    // with current note; Accept replaces editor content wholesale via onReplaceNote
    // (parent handles yjs-aware setContents); Cancel returns to source picker.
    // Full-screen overlay (not panel-docked) to give real reading space.
    const currentLen = (previewBaseText || '').length;
    const masterLen = (masterNoteOutput || '').length;
    const metaBits = [];
    if (masterNoteMetadata?.model_used) metaBits.push(masterNoteMetadata.model_used);
    if (typeof masterNoteMetadata?.latency_seconds === 'number') {
      metaBits.push(`${masterNoteMetadata.latency_seconds.toFixed(1)}s`);
    }
    if (typeof masterNoteMetadata?.cost_usd === 'number') {
      metaBits.push(`$${masterNoteMetadata.cost_usd.toFixed(4)}`);
    }
    if (masterNoteMetadata?.output_tokens) {
      metaBits.push(`${masterNoteMetadata.output_tokens} tok`);
    }
    // ── Change highlighting ──────────────────────────────────────────────
    // A word-level diff is useless for a whole-note rewrite: every sentence
    // differs textually even when clinically unchanged, so a classic diff
    // lights up the entire document. Instead highlight what the physician
    // actually scans for before accepting:
    //   GREEN BLOCK  — "**Today's visit (date):**" blocks: today's new
    //                  content by prompt convention
    //   GREEN BORDER — paragraphs with no counterpart in the current note
    //                  (4-word-shingle novelty check, no deps)
    //   AMBER INLINE — "[edit: ...]" markers: physician action items
    const shingleWords = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
    const currentShingles = (() => {
      const words = shingleWords(previewBaseText);
      const set = new Set();
      for (let i = 0; i + 4 <= words.length; i += 1) set.add(words.slice(i, i + 4).join(' '));
      return set;
    })();
    const blockNovelty = (block) => {
      const words = shingleWords(block);
      if (words.length < 6) return 0;
      let total = 0;
      let hits = 0;
      for (let i = 0; i + 4 <= words.length; i += 1) {
        total += 1;
        if (currentShingles.has(words.slice(i, i + 4).join(' '))) hits += 1;
      }
      return total ? 1 - hits / total : 0;
    };
    const renderEditMarkers = (text) => {
      const parts = String(text).split(/(\[edit:[^\]]*\])/g);
      if (parts.length === 1) return text;
      return parts.map((part, i) => (
        /^\[edit:/.test(part)
          ? <mark key={i} className="rounded bg-amber-200 px-0.5 text-amber-900 dark:bg-amber-500/30 dark:text-amber-200">{part}</mark>
          : part
      ));
    };
    const masterBlocks = String(masterNoteOutput || '').split(/\n{2,}/);
    return createPortal(
      <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4">
        <div className="pointer-events-auto flex h-[min(90vh,900px)] w-[min(96vw,1400px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2">
                Master Note preview
                {/* Reports the skill the BACKEND actually applied to this note
                    (echoed in metadata), not merely what is selected now —
                    the two differ once the picker is changed post-generate. */}
                {(() => {
                  const applied = skillBadgeLabel(
                    masterNoteMetadata?.activeSkill,
                    masterNoteMetadata?.skillIntensity,
                  );
                  if (!applied) return null;
                  return (
                    <span
                      data-testid="master-preview-skill-badge"
                      title="This note was generated with the skill applied"
                      className="rounded px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300"
                    >
                      🪨 {applied}
                    </span>
                  );
                })()}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Whole-note rewrite. Accept to replace current note. Cancel to keep current.
                {metaBits.length > 0 && (
                  <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {metaBits.join(' · ')}
                  </span>
                )}
                {/* Sits with the other run metadata rather than under the Save
                    button, where it would shift the footer row's alignment. */}
                {savedAtStamp && (
                  <span
                    data-testid="saved-diff-stamp"
                    className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300"
                  >
                    💾 Saved {formatSavedAt(savedAtStamp)}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                onClick={() => setShowMasterDiff(true)}
              >
                View diff
              </button>
              <button
                type="button"
                className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                onClick={() => {
                  setScreen('sources');
                  leaveMasterPreview();
                }}
              >
                Back to sources
              </button>
              {/* The only close-the-modal affordance on this screen since
                  "Keep for later" was removed. Clears the preview like Back
                  to sources does, so a note generated for one chart can't
                  still be sitting here when a different chart re-opens the
                  modal. An unsaved note is discarded — "Save for later" is
                  the way to keep one. */}
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                aria-label="Close Master Note preview"
                onClick={() => {
                  setScreen('sources');
                  leaveMasterPreview();
                  if (typeof onClose === 'function') onClose();
                }}
              >✕</button>
            </div>
          </div>
          {savedDiffStale && (
            /* A restored diff was composed against a note that has since moved
               on — most likely edited on the other device. Accept & replace
               still overwrites wholesale (unchanged behaviour); this makes
               sure that isn't a surprise. */
            <div
              data-testid="saved-diff-stale-warning"
              className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200"
            >
              ⚠ This note has <strong>changed since</strong> the diff was saved. Accepting replaces the
              current note with the proposed one, discarding edits made in between.
            </div>
          )}
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-0 divide-x divide-slate-200 dark:divide-slate-700">
            <div className="flex min-h-0 flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                <span>{restoredBaseText !== null ? 'Note when the diff was saved' : 'Current note'}</span>
                <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400">{currentLen.toLocaleString()} chars</span>
              </div>
              <pre className="flex-1 overflow-auto whitespace-pre-wrap break-words px-3 py-2 text-xs text-slate-800 dark:text-slate-200">{previewBaseText || '(empty)'}</pre>
            </div>
            <div className="flex min-h-0 flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 dark:border-slate-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                <span>Master Note (proposed)</span>
                <span className="flex items-center gap-2 font-mono text-[10px] text-emerald-600 dark:text-emerald-400">
                  <span className="flex items-center gap-1 font-sans text-slate-500 dark:text-slate-400">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-200 dark:bg-emerald-500/40" /> new
                    <span className="ml-1 inline-block h-2.5 w-2.5 rounded-sm bg-amber-200 dark:bg-amber-500/40" /> needs input
                  </span>
                  {masterLen.toLocaleString()} chars
                </span>
              </div>
              <div className="flex-1 overflow-auto px-3 py-2 text-xs text-slate-800 dark:text-slate-200">
                {masterBlocks.length === 1 && !masterBlocks[0]
                  ? <pre className="whitespace-pre-wrap">(empty)</pre>
                  : masterBlocks.map((block, i) => {
                      const isTodaysVisit = /\*\*Today'?s visit/i.test(block);
                      const isNovel = !isTodaysVisit && blockNovelty(block) >= 0.6;
                      let cls = 'mb-3 whitespace-pre-wrap break-words';
                      if (isTodaysVisit) {
                        cls += ' rounded-md bg-emerald-50 px-2 py-1.5 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:ring-emerald-500/30';
                      } else if (isNovel) {
                        cls += ' border-l-[3px] border-emerald-300 bg-emerald-50/40 pl-2 dark:border-emerald-500/50 dark:bg-emerald-950/20';
                      }
                      return <div key={i} className={cls}>{renderEditMarkers(block)}</div>;
                    })}
              </div>
            </div>
          </div>
          <div className="shrink-0 border-t border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/50">
            {masterFeedbackSubmitted ? (
              <div className="text-emerald-700 dark:text-emerald-300">Thanks for the feedback.</div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">Was this note helpful?</span>
                  <button
                    type="button"
                    aria-label="Note helpful: Yes"
                    className={`rounded-md border px-2 py-1 text-xs ${masterFeedbackStatus === 'positive' ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200'}`}
                    onClick={() => { setMasterFeedbackStatus('positive'); setMasterFeedbackError(null); }}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    aria-label="Note helpful: No"
                    className={`rounded-md border px-2 py-1 text-xs ${masterFeedbackStatus === 'negative' ? 'border-rose-600 bg-rose-600 text-white' : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200'}`}
                    onClick={() => { setMasterFeedbackStatus('negative'); setMasterFeedbackError(null); }}
                  >
                    No
                  </button>
                  {masterFeedbackStatus && (
                    <>
                      <button
                        type="button"
                        className="ml-auto rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
                        onClick={() => {
                          setMasterFeedbackStatus(null);
                          setMasterFeedbackText('');
                          setMasterFeedbackError(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        aria-label="Submit note feedback"
                        disabled={submittingMasterFeedback}
                        className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                        onClick={submitMasterFeedback}
                      >
                        {submittingMasterFeedback ? 'Submitting...' : 'Submit'}
                      </button>
                    </>
                  )}
                </div>
                {masterFeedbackStatus && (
                  <textarea
                    className="min-h-[52px] w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                    placeholder="Optional: Tell us more about your feedback..."
                    value={masterFeedbackText}
                    onChange={(e) => setMasterFeedbackText(e.target.value)}
                  />
                )}
                {masterFeedbackError && <div className="text-xs text-rose-600">{masterFeedbackError}</div>}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
            <button
              type="button"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              onClick={() => {
                try {
                  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(masterNoteOutput || '');
                  }
                } catch (_) { /* clipboard unavailable — silently skip */ }
              }}
            >
              Copy to clipboard
            </button>
            <div className="flex items-center gap-2">
              {/* "Save for later" lived here. Every run with a document path is
                  now saved by the backend before it replies, so there is
                  nothing to save by hand: say so instead. */}
              {masterNoteRequestId && (
                <span className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="master-note-autosaved">
                  Saved automatically. Open it later here or on your phone.
                </span>
              )}
              {/* "Keep for later" lived here (Omar 2026-08-17: removed). It
                  parked the note in React state only — no storage at all, so
                  it died on reload/tab close. The saved Master Note (a real
                  per-user file, kept by the backend) replaces it. Parking itself is untouched:
                  a generation that finishes while the modal is closed still
                  auto-parks to the sidebar (see generate() and
                  dismissSourcesWindow). */}
              <button
                type="button"
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!masterNoteOutput || typeof onReplaceNote !== 'function'}
                onClick={() => {
                  if (typeof onReplaceNote === 'function' && masterNoteOutput) {
                    try { onReplaceNote(masterNoteOutput, masterNoteSources); } catch (_) { /* parent handled */ }
                  }
                  // Accepting clears the saved Master Note (2026-10-29; it used
                  // to stay, Omar 2026-08-17): it is in the note now, and in
                  // Version History. Only this run's — a newer run started
                  // elsewhere is left alone.
                  if (typeof onAcceptedMasterNote === 'function') {
                    // A saved Master Note from before runs had ids has none: it
                    // is the one restored into this preview (restoredBaseText set).
                    try { onAcceptedMasterNote(masterNoteRequestId, { restoredSaved: restoredBaseText !== null }); } catch (_) { /* parent handled */ }
                  }
                  leaveMasterPreview();
                  setScreen('sources');
                  if (typeof onClose === 'function') onClose();
                }}
              >
                Accept & replace note
              </button>
            </div>
          </div>
        </div>
        {showMasterDiff && (
          <MasterNoteDiffModal
            currentText={previewBaseText}
            proposedText={masterNoteOutput}
            onClose={() => setShowMasterDiff(false)}
          />
        )}
      </div>,
      portalTarget,
    );
  }

  if (screen === 'review') {
    // Panel-mode: portal into the right-panel slot the chart provided,
    // skip the floating-window geometry, drop drag/resize. Falls back to
    // the floating window if the ref isn't ready (e.g. user dragged it
    // before the panel mounted).
    const panelSlotEl = diffPanelSlotRef && diffPanelSlotRef.current ? diffPanelSlotRef.current : null;
    // Always render review in the right-panel slot when available — covers
    // both the autoGenerate flow (Audio Manager, Reports per-row button) AND
    // the manual top-bar Process flow. The chart-page ensures the slot is in
    // the DOM by switching the right panel to the Copilot tab whenever Process
    // is open, so panel mode kicks in for every Process path. Falls back to
    // the floating window only if the ref isn't mounted yet.
    const panelMode = !!panelSlotEl;
    const renderTarget = panelMode ? panelSlotEl : portalTarget;
    return createPortal(
      <div className={panelMode ? 'pointer-events-auto absolute inset-0' : 'pointer-events-none fixed inset-0 z-[55]'}>
        <div
          className={panelMode
            ? 'pointer-events-auto absolute inset-0 flex flex-col overflow-hidden bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100'
            : 'pointer-events-auto fixed flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'}
          style={panelMode ? undefined : {
            left: reviewWindow.left,
            top: reviewWindow.top,
            width: reviewWindow.width,
            height: reviewWindow.height,
          }}
        >
          <div
            className={panelMode
              ? 'flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-2 dark:border-slate-700'
              : 'flex cursor-move items-center justify-between gap-3 border-b border-slate-200 px-4 py-2 dark:border-slate-700'}
            onMouseDown={panelMode ? undefined : startDragging}
          >
            <div>
              <h2 className="text-sm font-semibold">Review changes</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Drag to move. Resize from the edges or corners. The page stays interactive.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Task #J: AI debug button hidden in production — dev noise.
                  Surface via ?debug=1 query string if a dev needs it. */}
              {debugInfo && new URLSearchParams(window.location.search).has('debug') && (
                <button
                  type="button"
                  className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700"
                  onClick={() => setShowDebug(true)}
                >
                  View AI debug
                </button>
              )}
              {/* Park in sidebar — lift the current review state to the
                  parent (via onParkReview) so it surfaces as its own tab
                  in the right sidebar (alongside AI Chat / Reports / Live
                  Transcript / Copilot), then close this modal. The user
                  keeps editing the note while the diff waits for review.
                  Hidden only when a review is ALREADY parked so the user
                  doesn't silently overwrite the existing payload. */}
              {onParkReview && !hasParkedReview && (
                <button
                  type="button"
                  className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-300 dark:hover:bg-indigo-950/50"
                  onClick={() => {
                    if (typeof onParkReview !== 'function') return;
                    onParkReview({
                      edits,
                      approvals,
                      debugInfo,
                      generatedRequestItems,
                      selectedItems,
                      documentText,
                    });
                    onClose();
                  }}
                  aria-label="Park review in sidebar"
                  title="Send this diff to the right sidebar as a tab so you can keep editing the note"
                >
                  <span className="inline-flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8l3-3M5 8l3 3M19 16H5m14 0l-3-3m3 3l-3 3" />
                    </svg>
                    Park in sidebar
                  </span>
                </button>
              )}
              {/* Task #J: "Back to sources" reduced to an icon-only ← button
                  (was a word-heavy chip cluttering the header). Same action:
                  return to the source-picker screen. */}
              <button
                type="button"
                className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                onClick={() => setScreen('sources')}
                aria-label="Back to source selection"
                title="Back to source selection"
              >
                ←
              </button>
              <button type="button" className="text-slate-400" onClick={onClose} aria-label="Close review window">✕</button>
            </div>
          </div>
          {error && <div className="px-4 pt-3 text-sm text-rose-600">{error}</div>}
          <div className="relative flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4">
            {/* Task #229 (Leejoe 2026-06-29): Rewrite/Add-on tab toggle
                removed for Foothill Thursday demo. Rewrite is the only
                mode shipped; backend overlay_mode='overwrite' is the
                default. Add-on parking lot — re-enable as a per-physician
                setting later (see project_diff_modes_future.md memory). */}
            <div className="min-h-0 flex-1">
              <ProcessDiffReview
                text={documentText} locatedEdits={edits} approvals={approvals}
                onApprove={(id) => setApprovals((a) => ({ ...a, [id]: 'approved' }))}
                onDeny={(id) => setApprovals((a) => ({ ...a, [id]: 'denied' }))}
                onEditSave={(id, newText) => {
                  // Clinician refined the AI suggestion in-place. Update the
                  // edit's newText so composeOps applies their version, and
                  // mark the matchLen so a previously pure-deletion edit
                  // (matchLen=0 newText='') becomes a replacement when they
                  // typed something to keep.
                  setEdits((prev) => prev.map((e) => {
                    if (e.id !== id) return e;
                    const updated = { ...e, newText };
                    if (!e.newText && e.oldText && newText) {
                      updated.matchLen = e.oldText.length;
                    }
                    return updated;
                  }));
                }}
                onApproveRest={() => setApprovals((prev) => {
                  // Approve every edit that's still pending; LEAVE denied ones
                  // alone. Per Leejoe live-test: Accept All used to silently
                  // revert his X choices to checks. Now it's an additive bulk
                  // action — if you rejected a few, "Approve rest" picks up
                  // the leftovers, not the ones you already X'd.
                  const next = { ...prev };
                  for (const e of edits) {
                    if (next[e.id] !== 'denied') next[e.id] = 'approved';
                  }
                  return next;
                })}
                onRejectAllClose={async () => {
                  // "Reject all & mark reviewed" — stamp the ledger via the
                  // apply path (with no approved edits) so this source greys
                  // out for next time.
                  const denied = Object.fromEntries(edits.map((e) => [e.id, 'denied']));
                  setApprovals(denied);
                  setApplying(true);
                  setError(null);
                  try {
                    await onApplyEdits([], selectedItems);
                    onClose();
                  } catch (e) {
                    setError(e.message || 'Failed to mark reviewed.');
                  } finally {
                    setApplying(false);
                  }
                }}
                onApply={apply} applying={applying}
              />
            </div>
            <div className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/50">
              {diffFeedbackSubmitted ? (
                <div className="text-emerald-700 dark:text-emerald-300">Thanks for the feedback.</div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">Was this diff helpful?</span>
                    <button
                      type="button"
                      aria-label="Diff helpful: Yes"
                      className={`rounded-md border px-2 py-1 text-xs ${diffFeedbackStatus === 'positive' ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200'}`}
                      onClick={() => { setDiffFeedbackStatus('positive'); setDiffFeedbackError(null); }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      aria-label="Diff helpful: No"
                      className={`rounded-md border px-2 py-1 text-xs ${diffFeedbackStatus === 'negative' ? 'border-rose-600 bg-rose-600 text-white' : 'border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200'}`}
                      onClick={() => { setDiffFeedbackStatus('negative'); setDiffFeedbackError(null); }}
                    >
                      No
                    </button>
                    {diffFeedbackStatus && (
                      <>
                        <button
                          type="button"
                          className="ml-auto rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
                          onClick={() => {
                            setDiffFeedbackStatus(null);
                            setDiffFeedbackText('');
                            setDiffFeedbackError(null);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          aria-label="Submit diff feedback"
                          disabled={submittingDiffFeedback}
                          className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                          onClick={submitDiffFeedback}
                        >
                          {submittingDiffFeedback ? 'Submitting...' : 'Submit'}
                        </button>
                      </>
                    )}
                  </div>
                  {diffFeedbackStatus && (
                    <textarea
                      className="min-h-[52px] w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                      placeholder="Optional: Tell us more about your feedback..."
                      value={diffFeedbackText}
                      onChange={(e) => setDiffFeedbackText(e.target.value)}
                    />
                  )}
                  {diffFeedbackError && <div className="text-xs text-rose-600">{diffFeedbackError}</div>}
                </div>
              )}
            </div>
            {showDebug && <ProcessDebugPanel debug={debugInfo} onClose={() => setShowDebug(false)} />}
          </div>
          {!panelMode && (
            <>
              <div onMouseDown={startResizing('n')} className="absolute left-4 right-4 top-0 z-10 h-1.5 cursor-ns-resize" title="Resize" />
              <div onMouseDown={startResizing('s')} className="absolute bottom-0 left-4 right-4 z-10 h-1.5 cursor-ns-resize" title="Resize" />
              <div onMouseDown={startResizing('w')} className="absolute bottom-4 left-0 top-4 z-10 w-1.5 cursor-ew-resize" title="Resize" />
              <div onMouseDown={startResizing('e')} className="absolute bottom-4 right-0 top-4 z-10 w-1.5 cursor-ew-resize" title="Resize" />
              <div onMouseDown={startResizing('nw')} className="absolute left-0 top-0 z-20 h-4 w-4 cursor-nwse-resize" title="Resize" />
              <div onMouseDown={startResizing('ne')} className="absolute right-0 top-0 z-20 h-4 w-4 cursor-nesw-resize" title="Resize" />
              <div onMouseDown={startResizing('sw')} className="absolute bottom-0 left-0 z-20 h-4 w-4 cursor-nesw-resize" title="Resize" />
              <div
                onMouseDown={startResizing('se')}
                title="Drag to resize"
                className="absolute bottom-0 right-0 z-20 h-4 w-4 cursor-nwse-resize"
                style={{
                  background:
                    'linear-gradient(135deg, transparent 50%, rgba(100,116,139,0.55) 50%, rgba(100,116,139,0.55) 60%, transparent 60%, transparent 70%, rgba(100,116,139,0.55) 70%, rgba(100,116,139,0.55) 80%, transparent 80%)',
                }}
              />
            </>
          )}
        </div>
      </div>,
      renderTarget,
    );
  }

  // Source-picker screen: same panel-mode pattern as the review screen. When
  // the chart's right panel has provided a slot ref (processDiffInPanelMode is
  // on), render INTO the slot instead of as a floating centered modal. This
  // is what gets the picker out from behind the external recorder popup —
  // recorder is a separate browser window so chart-side z-index can never beat
  // it; only DOM-being-in-the-right-panel does. Falls back to floating if no
  // slot is available (e.g. user closed the right panel manually).
  const sourcesPanelSlot = diffPanelSlotRef && diffPanelSlotRef.current ? diffPanelSlotRef.current : null;
  const sourcesPanelMode = !!sourcesPanelSlot;
  const sourcesRenderTarget = sourcesPanelMode ? sourcesPanelSlot : portalTarget;
  // Unified dismiss for the sources window — the backdrop click-away and the
  // ✕ button MUST run the same park-before-close logic. A finished Master
  // Note costs ~60-90s + an API run, so dismissal auto-parks it instead of
  // discarding; a dismissal DURING generation parks a live GENERATING
  // placeholder so the run stays visible in the Review tab from the moment
  // the modal disappears (Leejoe 2026-07-07: "should park not just AFTER
  // generating the diff but even as soon as i click other tabs and it
  // disappears"). This is now the only implicit park path — the footer's
  // "Keep for later" button was removed in favour of "Save for later", which
  // writes a real file rather than in-memory state.
  const dismissSourcesWindow = () => {
    if (screen === 'master-preview' && masterNoteOutput && typeof onParkReview === 'function' && !hasParkedReview) {
      onParkReview({ phase: 'master', masterNoteOutput, masterNoteMetadata, selectedItems, documentText });
    } else if (masterGenInFlightRef.current && typeof onParkReview === 'function' && parkedPhase !== 'review') {
      onParkReview({ phase: 'master', masterNoteOutput: '', masterGenerating: true, selectedItems, documentText });
    }
    onClose();
  };
  return createPortal(
    <div
      className={sourcesPanelMode
        ? 'absolute inset-0 flex flex-col'
        : 'fixed inset-0 z-50 flex items-center justify-center bg-black/40'}
      onClick={sourcesPanelMode ? undefined : dismissSourcesWindow}
    >
      <div
        className={sourcesPanelMode
          ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 p-4 flex-1 flex flex-col overflow-hidden'
          : 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-xl shadow-xl p-4 w-[640px] max-w-[92vw] max-h-[90vh] flex flex-col'
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 shrink-0">
          <h2 className="text-base font-semibold flex items-center gap-2">
            {screen === 'sources' ? 'Process sources' : 'Review changes'}
            {/* Active-skill badge — the skill rewrites the generated note, so
                it is stated at the top rather than only inside the picker. */}
            {skillHeaderLabel && (
              <span
                data-testid="process-skill-badge"
                title="Skill active — it shapes the wording of the generated note"
                className="rounded px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300"
              >
                🪨 {skillHeaderLabel}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            {/* Park-in-sidebar button — same affordance from both the
                source-picker AND review screens so the user can minimize
                the modal at any point in the Process flow. From the
                sources screen we capture the selection state (selected,
                expanded, useTemplate, pins); from the review screen we
                capture the AI's edits/approvals. Either way the parent
                re-opens the work via the right bar's "Review" tab.
                Hidden only when a review is ALREADY parked so the user
                doesn't silently overwrite the existing payload. */}
            {onParkReview && !hasParkedReview && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-300 dark:hover:bg-indigo-950/50"
                onClick={() => {
                  if (typeof onParkReview !== 'function') return;
                  if (screen === 'sources') {
                    // Capture the source-picker state so the right panel
                    // restores the user's selection (selectedIds,
                    // expanded groups, useTemplate toggle) when they
                    // re-open the Review tab.
                    onParkReview({
                      phase: 'sources',
                      sourceGroups: groups,
                      selectedIds: Array.from(selectedIds),
                      expanded: Array.from(expanded),
                      useTemplate,
                      pinAudioSessionId,
                      pinReportPath,
                      autoGenerate,
                    });
                  } else if (screen === 'master-preview' && masterNoteOutput) {
                    onParkReview({
                      phase: 'master',
                      masterNoteOutput,
                      masterNoteMetadata,
                      selectedItems,
                      documentText,
                    });
                  } else {
                    onParkReview({
                      phase: 'review',
                      edits,
                      approvals,
                      debugInfo,
                      generatedRequestItems,
                      selectedItems,
                      documentText,
                    });
                  }
                  onClose();
                }}
                aria-label="Park process in sidebar"
                title="Send this to the right sidebar as a tab so you can keep editing the note"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8l3-3M5 8l3 3M19 16H5m14 0l-3-3m3 3l-3 3" />
                </svg>
                Park in sidebar
              </button>
            )}
            <button
              type="button"
              className="text-slate-400"
              onClick={dismissSourcesWindow}
              aria-label="Close modal"
            >✕</button>
          </div>
        </div>
        {error && <div className="mb-2 text-sm text-rose-600 shrink-0">{error}</div>}
        {/* A Master Note this user saved for this document — possibly from
            another device. Sits above the source picker so it's the first
            thing seen on re-entry, rather than being re-generated by accident. */}
        {/* A run still going — here, in another tab or on the phone. The
            chart re-checks it every few seconds; its result lands in the
            saved banner below when it finishes. */}
        {screen === 'sources' && runningRun && !isGenerating && (
          <div
            data-testid="saved-run-running"
            className="mb-3 shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            ⏳ A Master Note for this note is being generated{runOrigin(runningRun)}
            {runningRun.startedAt ? ` (started ${formatSavedAt(runningRun.startedAt)})` : ''}. It appears here when it finishes.
          </div>
        )}
        {screen === 'sources' && !runningRun && (failedRun || savedDiff?.lastFailure) && (
          <div
            data-testid="saved-run-failed"
            className="mb-3 shrink-0 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300"
          >
            The last Master Note{runOrigin(failedRun || savedDiff?.lastFailure)} did not finish: {(failedRun || savedDiff.lastFailure).error || 'generation failed'}.
            {savedDiff ? ' Your earlier saved Master Note is still below.' : ' Generate again to retry.'}
            {failedRun && typeof onDiscardSavedDiff === 'function' && (
              <button type="button" className="ml-2 underline" onClick={() => onDiscardSavedDiff()}>Dismiss</button>
            )}
          </div>
        )}
        {screen === 'sources' && savedDiff?.proposedText && (
          <div
            data-testid="saved-diff-banner"
            className="mb-3 shrink-0 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 dark:border-indigo-800 dark:bg-indigo-950/30"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-indigo-800 dark:text-indigo-200">
                  💾 Saved Master Note
                </div>
                <div className="text-[11px] text-indigo-700 dark:text-indigo-300">
                  Saved {formatSavedAt(savedDiff.savedAt)}{runOrigin(savedDiff)} · not applied to the note yet
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  aria-label="Open saved diff"
                  className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                  onClick={openSavedDiff}
                >
                  Open
                </button>
                <button
                  type="button"
                  aria-label="Delete saved diff"
                  className="rounded-md border border-indigo-300 px-2.5 py-1 text-xs text-indigo-700 hover:bg-indigo-100 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/50"
                  onClick={handleDiscardSavedDiff}
                >
                  Delete
                </button>
              </div>
            </div>
            {savedDiffError && (
              <div className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">{savedDiffError}</div>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0 flex flex-col">
          {loading ? (
            <div className="text-sm text-slate-500">Loading sources…</div>
          ) : screen === 'sources' ? (
            <div className="flex-1 min-h-0 overflow-auto">
              <ProcessSourceList
                groups={groups} selectedIds={selectedIds} expanded={expanded} useTemplate={useTemplate}
                onToggleItem={toggleItem} onToggleGroup={toggleGroup} onToggleExpand={toggleExpand}
                onToggleTemplate={() => setUseTemplate((v) => !v)} onResetSelection={resetSelection}
                onSelectOnlyNew={selectOnlyNew} onClearAll={clearAllSelection}
                onGenerate={generate}
                generating={isGenerating}
                generationStartedAt={generationStartedAt}
                activeSkill={activeSkill}
                skillIntensity={skillIntensity}
                onSkillChange={onSkillChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    sourcesRenderTarget,
  );
}
